<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Doctor Specialities Management' : 'Deleted Doctor Specialities'"
              :subtitle="isDeletePage() != true ? 'Manage doctor specializations and medical expertise' : 'Restore or permanently delete doctor specialities'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/doctor_specialities/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Speciality
                    </v-btn>
                    <v-btn
                      color="success"
                      outlined
                      small
                      @click="exportToExcel"
                      class="px-3"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Doctor Specialities</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Specialities"
                    prepend-inner-icon="mdi-magnify"
                    solo
                    rounded
                    clearable
                    placeholder="Doctor name, specialization..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    solo
                    rounded
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Doctor Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.doctor_id"
                    :items="doctors"
                    label="Doctor"
                    prepend-inner-icon="mdi-doctor"
                    solo
                    rounded
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Specialization Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.specialization_id"
                    :items="specializations"
                    label="Specialization"
                    prepend-inner-icon="mdi-medical-bag"
                    solo
                    rounded
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Doctor Specialities Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Specialities List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-medical-bag</v-icon>
                      {{ count }} {{ count === 1 ? "Speciality" : "Specialities" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage()"
                      x-small
                      color="success"
                      outlined
                      @click="exportToExcel"
                      class="px-2"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading specialities..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="doctorSpecialities"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                fixed-header
                height="285"
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.is_active ? "mdi-check-circle" : "mdi-close-circle"
                      }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Doctor Info Column -->
                <template #[`item.doctor_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <v-img
                        v-if="item.doctor && item.doctor.image_path"
                        :src="getImageUrl(item.doctor.image_path)"
                        :alt="item.doctor.first_name"
                      ></v-img>
                      <v-icon v-else color="primary">mdi-doctor</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">
                        {{
                          item.doctor
                            ? `${item.doctor.first_name} ${item.doctor.last_name}`
                            : "N/A"
                        }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.doctor ? item.doctor.email : "N/A" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Specialization Column -->
                <template #[`item.specialization_info`]="{ item }">
                  <div>
                    <div class="font-weight-medium">
                      {{ item.specialization ? item.specialization.name : "N/A" }}
                    </div>
                    <div class="text-caption grey--text">
                      {{
                        item.specialization && item.specialization.description
                          ? item.specialization.description
                          : "No description"
                      }}
                    </div>
                  </div>
                </template>

                <!-- Created Date Column -->
                <template #[`item.created_at`]="{ item }">
                  <div>
                    <div class="text-caption">
                      {{ formatDate(item.created_at) }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ formatTime(item.created_at) }}
                    </div>
                  </div>
                </template>

                <!-- Created By Column -->
                <template #[`item.created_by`]="{ item }">
                  <div class="text-caption">
                    {{
                      item.created_by
                        ? `${item.created_by.first_name} ${item.created_by.last_name}`
                        : "System"
                    }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex align-center">
                    <template v-if="!isDeletePage()">
                      <v-menu offset-y>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            outlined
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-dots-vertical</v-icon>
                            Actions
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item :to="`/doctor_specialities/${item.id}`">
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="primary">mdi-pencil</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption"
                                >Edit</v-list-item-title
                              >
                            </v-list-item-content>
                          </v-list-item>

                          <v-list-item
                            @click="Delete_this(item.id)"
                            class="error--text"
                          >
                            <v-list-item-icon class="mr-2">
                              <v-icon x-small color="red">mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption"
                                >Delete</v-list-item-title
                              >
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="Restore_this(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Doctor Speciality</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-medical-bag</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Doctor Specialities Found</h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No doctor specialities match your current filters. Try adjusting your search criteria."
                          : "You haven't added any doctor specialities yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/doctor_specialities/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Doctor Speciality
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-1"
                    >
                      <v-icon x-small>mdi-chevron-left</v-icon>
                    </v-btn>

                    <!-- Page Numbers -->
                    <template v-for="pageNumber in visiblePages">
                      <v-btn
                        :key="pageNumber"
                        x-small
                        :outlined="pageNumber !== page"
                        :color="pageNumber === page ? 'primary' : 'default'"
                        @click="page = pageNumber"
                        class="mx-1"
                      >
                        {{ pageNumber }}
                      </v-btn>
                    </template>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= totalPages"
                      @click="page++"
                      class="ml-1"
                    >
                      <v-icon x-small>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :max="totalPages"
                      min="1"
                      style="width: 60px"
                      dense
                      outlined
                      hide-details
                      @keydown.enter="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "doctorSpecialitiesPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        doctor_id: "",
        specialization_id: "",
        show_deleted: false,
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: null,
      isLoading: false,
      doctorSpecialities: [],
      doctors: [],
      specializations: [],
      statuses: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        {
          text: "Doctor Info",
          value: "doctor_info",
          sortable: false,
          width: "200px",
        },
        {
          text: "Specialization",
          value: "specialization_info",
          sortable: false,
          width: "200px",
        },
        {
          text: "Status",
          value: "is_active",
          sortable: false,
          width: "100px",
          align: "center",
        },
        {
          text: "Created Date",
          value: "created_at",
          sortable: false,
          width: "120px",
        },
        {
          text: "Created By",
          value: "created_by",
          sortable: false,
          width: "120px",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "120px",
          align: "center",
        },
      ],
    };
  },
  computed: {
    hasActiveFilters() {
      return !!(
        this.filter.search_keyword ||
        this.filter.is_active ||
        this.filter.doctor_id ||
        this.filter.specialization_id
      );
    },
    totalPages() {
      return Math.ceil(this.count / this.itemsPerPage);
    },
    visiblePages() {
      const totalPages = this.totalPages;
      const currentPage = this.page;
      const range = 2;
      let start = Math.max(1, currentPage - range);
      let end = Math.min(totalPages, currentPage + range);

      if (end - start < range * 2) {
        if (start === 1) {
          end = Math.min(totalPages, start + range * 2);
        } else {
          start = Math.max(1, end - range * 2);
        }
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
  },
  watch: {
    page() {
      this.getData();
    },
    itemsPerPage() {
      this.page = 1;
      this.getData();
    },
  },
  created() {
    this.getData();
    this.getDoctors();
    this.getSpecializations();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          ...this.filter,
        };

        // Remove empty filters
        Object.keys(params).forEach((key) => {
          if (params[key] === "" || params[key] === null) {
            delete params[key];
          }
        });

        const response = await axios.get("doctor_specialities", { params });
        
        this.doctorSpecialities = response.data.data;
        this.count = response.data.count;
      } catch (error) {
        console.error("Error fetching doctor specialities:", error);
        this.$toast.error("Failed to fetch doctor specialities");
      } finally {
        this.isLoading = false;
      }
    },

    async getDoctors() {
      try {
        const response = await axios.get("users", {
          params: { is_active: 1, rowsPerPage: 1000 },
        });
        this.doctors = response.data.data.map((doctor) => ({
          text: `${doctor.first_name} ${doctor.last_name}`,
          value: doctor.id,
        }));
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    },

    async getSpecializations() {
      try {
        const response = await axios.get("value_lists", {
          params: { category: "medical_specialization", rowsPerPage: 1000 },
        });
        this.specializations = response.data.data.map((specialization) => ({
          text: specialization.name,
          value: specialization.id,
        }));
      } catch (error) {
        console.error("Error fetching specializations:", error);
      }
    },

    async exportToExcel() {
      try {
        const params = {
          ...this.filter,
        };

        // Remove empty filters
        Object.keys(params).forEach((key) => {
          if (params[key] === "" || params[key] === null) {
            delete params[key];
          }
        });

        const response = await axios.get("doctor_specialities/excel", {
          params,
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `doctor_specialities_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.$toast.success("Export completed successfully");
      } catch (error) {
        console.error("Error exporting data:", error);
        this.$toast.error("Failed to export data");
      }
    },

    async Delete_this(id) {
      if (
        await this.$confirm("Are you sure you want to delete this doctor speciality?")
      ) {
        try {
          await axios.post(`doctor_specialities/delete/${id}`);
          this.$toast.success("Doctor speciality deleted successfully");
          this.getData();
        } catch (error) {
          console.error("Error deleting doctor speciality:", error);
          this.$toast.error("Failed to delete doctor speciality");
        }
      }
    },

    async Restore_this(id) {
      if (
        await this.$confirm("Are you sure you want to restore this doctor speciality?")
      ) {
        try {
          await axios.post(`doctor_specialities/restore/${id}`);
          this.$toast.success("Doctor speciality restored successfully");
          this.getData();
        } catch (error) {
          console.error("Error restoring doctor speciality:", error);
          this.$toast.error("Failed to restore doctor speciality");
        }
      }
    },

    clearAllFilters() {
      this.filter = {
        search_keyword: "",
        is_active: "",
        doctor_id: "",
        specialization_id: "",
        show_deleted: false,
      };
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active) count++;
      if (this.filter.doctor_id) count++;
      if (this.filter.specialization_id) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
    },

    goToPage() {
      if (this.jumpToPage && this.jumpToPage <= this.totalPages) {
        this.page = parseInt(this.jumpToPage);
        this.jumpToPage = null;
      }
    },

    isDeletePage() {
      return this.$route.query.deleted === "true";
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    },

    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString();
    },

    getImageUrl(imagePath) {
      return `${this.$store.getters["settings/getMediaUrl"]}${imagePath}`;
    },
  },
};
</script>

<style scoped>
.enhanced-table {
  border-radius: 8px;
}

.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.enhanced-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.enhanced-table >>> .v-data-table-header th {
  font-weight: 600;
  color: #333;
}

.enhanced-table >>> .v-data-table tbody tr:hover {
  background-color: #f9f9f9;
}
</style>